import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";

// Template173: Mapa Golden Gate + contenido - bloques separados (tipBox, mapa, intro, batterySpencer, fortPoint, sausalito, bottomBox)
const Template173 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const [loaded, setLoaded] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const { setMapFullscreen } = useMapFullscreen() || {};

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const getCanvasImageUrl = (id) =>
		page.canvasImages?.find((img) => img.id === id)?.imageUrl ?? "";

	const headerText = page.texts?.[0]?.content ?? "";
	const introText = page.texts?.[1]?.content ?? "";
	const logoImage = getCanvasImageUrl("logo");
	const mapUrl = page.maps?.[0] ?? "";
	const offlineMapSrc = getImageSrc("sausalito");
	const tipBox =
		page?.tipBox ?? page?.data?.tipBox ?? page?.data?.data?.tipBox ?? {};
	const tipBoxText = tipBox.text ?? "";
	const tipBoxImageSrc = tipBox.imageId ? getImageSrc(tipBox.imageId) : "";
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const batterySpencer = sections[0] ?? {};
	const fortPoint = sections[1] ?? {};
	const sausalito = sections[2] ?? {};
	const bottomBox =
		page?.bottomBox ??
		page?.data?.bottomBox ??
		page?.data?.data?.bottomBox ??
		{};
	const actualHeaderColor = headerBackgroundColor || "#F2B705";

	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box data-block-id="map" sx={{ marginTop: "-70px", position: "relative" }}>
				{tipBoxText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 70,
							right: 5,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: "'Poppins', sans-serif",
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 120,
							zIndex: 3,
						}}
						dangerouslySetInnerHTML={{ __html: tipBoxText }}
					/>
				)}
				{tipBoxImageSrc && (
					<Box
						component="img"
						src={tipBoxImageSrc}
						alt=""
						sx={{
							width: 110,
							position: "absolute",
							bottom: 0,
							right: -10,
							zIndex: 2,
						}}
					/>
				)}

				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
							marginTop: "30px",
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}

				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa Golden Gate"
						src={mapUrl}
						width="100%"
						height={isSmallScreen ? "400px" : "340px"}
						allowFullScreen
						loading="lazy"
						onLoad={() => setLoaded(true)}
						sx={{ border: 0 }}
					/>
				) : offlineMapSrc ? (
					<Box
						component="img"
						src={offlineMapSrc}
						alt=""
						sx={{
							width: "100%",
							height: isSmallScreen ? "400px" : "340px",
							objectFit: "cover",
							position: "relative",
							zIndex: 2,
						}}
					/>
				) : !isOnline ? (
					<SinConexionKawaii
						height="100%"
						fontFamily={page?.fontFamily || "inherit"}
					/>
				) : null}

				{isOnline && mapUrl && (
					<Box
						component="button"
						onClick={() => setFullscreen(true)}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							fontFamily: "'Poppins', sans-serif",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}
			</Box>

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px", position: "relative" }}>
					<Box
						component="p"
						sx={{
							fontFamily: "'Poppins', sans-serif",
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				sx={{ padding: "10px 12px", position: "relative", paddingTop: "0px" }}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{/* ——— Battery Spencer (Mirador) ——— */}
					<Box data-block-id="text-2" sx={{ display: "flex", gap: 2, position: "relative" }}>
						{getImageSrc(batterySpencer.imageId) && (
							<Box
								component="img"
								src={getImageSrc(batterySpencer.imageId)}
								alt=""
								sx={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							{batterySpencer.title && (
								<Box component="h3" sx={estiloH3}>
									{batterySpencer.title}
								</Box>
							)}
							{batterySpencer.body && (
								<Box component="p" sx={estiloP}>
									{batterySpencer.body}
								</Box>
							)}
						</Box>
					</Box>

					{/* ——— Sausalito ——— */}
					<Box data-block-id="text-3" sx={{ display: "flex", gap: 2, position: "relative" }}>
						{/* ——— Fort Point (tooltip + imagen) ——— */}
						{(fortPoint.tooltipText || fortPoint.tooltipImageId) && (
							<Box
								component="img"
								src={getImageSrc(fortPoint.tooltipImageId)}
								alt=""
								sx={{
									width: 110,
									position: "absolute",
									bottom: isSmallScreen ? -80 : -30,
									right: -20,
									zIndex: 2,
								}}
							/>
						)}
						{(fortPoint.tooltipText || fortPoint.tooltipImageId) && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? -50 : -10,
									right: 80,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: "'Poppins', sans-serif",
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 200,
									zIndex: 3,
								}}
								dangerouslySetInnerHTML={{ __html: fortPoint.tooltipText }}
							></Box>
						)}

						{getImageSrc(sausalito.imageId) && (
							<Box
								component="img"
								src={getImageSrc(sausalito.imageId)}
								alt=""
								sx={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: "60%" }}>
							{sausalito.title && (
								<Box component="h3" sx={estiloH3}>
									{sausalito.title}
								</Box>
							)}
							{sausalito.body && (
								<Box component="p" sx={estiloP}>
									{sausalito.body}
								</Box>
							)}
						</Box>
						{Array.isArray(sausalito.sabiasQue) &&
							sausalito.sabiasQue.map((sq, i) => (
								<Box
									key={i}
									sx={{
										position: "absolute",
										top: i === 0 ? 40 : -10,
										left: "33%",
										zIndex: 10,
									}}
								>
									<SabiasQue icono={sq.icono} texto={sq.texto} />
								</Box>
							))}
					</Box>
				</Box>
			</Box>

			{/* ——— Ferry de vuelta (bottomBox) ——— */}
			{bottomBox.body && (
				<Box
					data-block-id="text-4"
					sx={{
						padding: "10px 12px",
						position: "relative",
						marginTop: isSmallScreen ? 6 : "0px",
					}}
				>
					<Box
						sx={{
							backgroundColor: "#fbf0cf",
							padding: 1.5,
							borderRadius: "12px",
							display: "flex",
							gap: 2,
							alignItems: "center",
							position: "relative",
							marginTop: 0,
						}}
					>
						<Box sx={{ paddingLeft: 1 }}>
							<Box component="p" sx={estiloP}>
								{bottomBox.body}
							</Box>
						</Box>
						{bottomBox.sabiasQue?.[0] && (
							<Box
								sx={{
									position: "absolute",
									top: isSmallScreen ? -20 : -20,
									right: isSmallScreen ? "50%" : "50%",
									zIndex: 10,
								}}
							>
								<SabiasQue
									icono={bottomBox.sabiasQue[0].icono}
									texto={bottomBox.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

Template173.thumbnail = "/images/template-1.png";
export default Template173;
